/**
 * Shared formatting utilities
 */

// Keep this list to currencies that use 2 decimal places until the money helpers
// are generalized beyond the current minor-unit * 100 / 100 assumptions.
export const SUPPORTED_CURRENCIES = ['GBP', 'USD', 'EUR', 'NGN', 'GHS', 'KES', 'CAD', 'AUD', 'CNY'] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];
export const DEFAULT_CURRENCY: Currency = 'GBP';
export type NumberFoldingMode = 'auto' | 'compact' | 'full';

export interface FormatNumberOptions {
  minValue?: number;
  decimals?: number;
  showDecimalsForWhole?: boolean;
  thousandSuffix?: string;
  millionSuffix?: string;
  billionSuffix?: string;
  trillionSuffix?: string;
  folding?: NumberFoldingMode;
  screenWidth?: number;
  containerWidth?: number;
  maxCharacters?: number;
}

export type NumericDisplayValue = number | string | null | undefined;

export interface FormatSocialCountOptions extends FormatNumberOptions {}

export interface FormatCurrencyOptions extends FormatNumberOptions {
  currency?: string | null;
  showSymbol?: boolean;
  symbol?: string;
  locale?: string;
}

export interface FormatSupportedCurrencyValueOptions {
  currency?: string | null;
  locale?: string;
  fallback?: string;
  unavailableFallback?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface FormatSupportedMinorCurrencyOptions {
  currency?: string | null;
  locale?: string;
  fallback?: string;
  unavailableFallback?: string;
}

export interface ParseCompactNumberOptions {
  allowNegative?: boolean;
  integer?: boolean;
}

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  GBP: '£', USD: '$', EUR: '€', NGN: '₦', GHS: 'GH₵', KES: 'KSh', CAD: '$', AUD: '$', CNY: '¥',
};

const CURRENCY_LOCALES: Record<Currency, string> = {
  GBP: 'en-GB', USD: 'en-US', EUR: 'en-EU', NGN: 'en-NG', GHS: 'en-GH', KES: 'en-KE', CAD: 'en-CA', AUD: 'en-AU', CNY: 'zh-CN',
};

const COMPACT_NUMBER_UNITS = [
  { value: 1_000_000_000_000_000_000_000_000_000_000, suffix: 'No', aliases: ['nonillion', 'nonillions'] },
  { value: 1_000_000_000_000_000_000_000_000_000, suffix: 'Oc', aliases: ['octillion', 'octillions'] },
  { value: 1_000_000_000_000_000_000_000_000, suffix: 'Sp', aliases: ['septillion', 'septillions'] },
  { value: 1_000_000_000_000_000_000_000, suffix: 'Sx', aliases: ['sextillion', 'sextillions'] },
  { value: 1_000_000_000_000_000_000, suffix: 'Qi', aliases: ['quintillion', 'quintillions'] },
  { value: 1_000_000_000_000_000, suffix: 'Qa', aliases: ['quadrillion', 'quadrillions'] },
  { value: 1_000_000_000_000, suffix: 'T', aliases: ['trillion', 'trillions', 'tn'] },
  { value: 1_000_000_000, suffix: 'B', aliases: ['billion', 'billions', 'bn'] },
  { value: 1_000_000, suffix: 'M', aliases: ['million', 'millions', 'mn', 'mil'] },
  { value: 1_000, suffix: 'K', aliases: ['thousand', 'thousands'] },
];

const COMPACT_NUMBER_UNIT_BY_SUFFIX = new Map<string, number>(
  COMPACT_NUMBER_UNITS.flatMap((unit) => [
    [unit.suffix.toLowerCase(), unit.value] as const,
    ...unit.aliases.map((alias) => [alias, unit.value] as const),
  ]),
);

const COMPACT_NUMBER_PATTERN = /^([+-]?(?:(?:\d+(?:,\d{3})+)|\d+)?(?:\.\d+)?|\d+)\s*([a-z]+)?$/i;
const DEFAULT_COMPACT_MIN_VALUE = 1_000;

function formatUnknownCurrencyLabel(
  currency: string | null | undefined,
): string | null {
  if (typeof currency !== 'string') return null;
  const normalized = currency.trim().toUpperCase();
  return normalized.length > 0 ? normalized : null;
}

function formatUnknownMinorCurrency(
  value: NumericDisplayValue,
  options: Pick<FormatSupportedMinorCurrencyOptions, 'currency' | 'locale'>,
): string | null {
  const parsed = parseCompactNumber(value, { integer: true });
  if (parsed === null || !Number.isFinite(parsed)) return null;

  const currencyLabel = formatUnknownCurrencyLabel(options.currency);
  if (!currencyLabel) return null;

  const amount = parsed / 100;
  const numberLocale = options.locale || 'en-GB';
  return `${currencyLabel} ${amount.toLocaleString(numberLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatUnknownCurrencyValue(
  value: NumericDisplayValue,
  options: Pick<
    FormatSupportedCurrencyValueOptions,
    'currency' | 'locale' | 'minimumFractionDigits' | 'maximumFractionDigits'
  >,
): string | null {
  const parsed = parseCompactNumber(value);
  if (parsed === null || !Number.isFinite(parsed)) return null;

  const currencyLabel = formatUnknownCurrencyLabel(options.currency);
  if (!currencyLabel) return null;

  const numberLocale = options.locale || 'en-GB';
  return `${currencyLabel} ${parsed.toLocaleString(numberLocale, {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  })}`;
}

function getFinitePositiveNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function normalizeCurrency(
  value: string | null | undefined,
  fallback: Currency = DEFAULT_CURRENCY,
): Currency {
  const normalized = value?.trim().toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(normalized || '')
    ? (normalized as Currency)
    : fallback;
}

export function getSupportedCurrency(
  value: string | null | undefined,
): Currency | null {
  const normalized = value?.trim().toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(normalized || '')
    ? (normalized as Currency)
    : null;
}

export function getFirstSupportedCurrency(
  ...values: Array<string | null | undefined>
): Currency | null {
  for (const value of values) {
    const supportedCurrency = getSupportedCurrency(value);
    if (supportedCurrency) {
      return supportedCurrency;
    }
  }

  return null;
}

export function getCurrencySymbol(
  currency: string | null | undefined,
  fallback: Currency = DEFAULT_CURRENCY,
): string {
  return CURRENCY_SYMBOLS[normalizeCurrency(currency, fallback)];
}

export function getCurrencyLocale(
  currency: string | null | undefined,
  fallback: Currency = DEFAULT_CURRENCY,
): string {
  return CURRENCY_LOCALES[normalizeCurrency(currency, fallback)];
}

export function parseCompactNumber(
  value: NumericDisplayValue,
  options: ParseCompactNumberOptions = {},
): number | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    if (value < 0 && options.allowNegative === false) return null;
    return options.integer ? Math.trunc(value) : value;
  }

  if (typeof value !== 'string') return null;

  const normalized = value
    .trim()
    .replace(/GH₵|KSh|[£$€₦¥]/g, '')
    .replace(/\s+/g, '');

  if (!normalized) return null;

  const match = normalized.match(COMPACT_NUMBER_PATTERN);
  if (!match) return null;

  const rawNumber = match[1]?.replace(/,/g, '') ?? '';
  if (!rawNumber || rawNumber === '+' || rawNumber === '-') return null;

  const numeric = Number(rawNumber);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 0 && options.allowNegative === false) return null;

  const suffix = match[2]?.toLowerCase();
  const multiplier = suffix ? COMPACT_NUMBER_UNIT_BY_SUFFIX.get(suffix) : 1;
  if (multiplier === undefined) return null;

  const parsed = numeric * multiplier;
  if (!Number.isFinite(parsed)) return null;

  return options.integer ? Math.trunc(parsed) : parsed;
}

export function parseMajorCurrencyToMinor(
  value: NumericDisplayValue,
  options: { allowZero?: boolean } = {},
): number | null {
  const parsed = parseCompactNumber(value, { allowNegative: false });
  if (parsed === null || !Number.isFinite(parsed)) return null;
  if (parsed < 0 || (!options.allowZero && parsed <= 0)) return null;
  return Math.round(parsed * 100);
}

export function formatMinorCurrencyAsMajorString(
  value: NumericDisplayValue,
  fallback = '',
): string {
  const parsed = parseCompactNumber(value, { integer: true });
  if (parsed === null || !Number.isFinite(parsed)) return fallback;
  return (parsed / 100).toFixed(2);
}

export function formatMinorCurrency(
  value: NumericDisplayValue,
  options: Pick<FormatCurrencyOptions, 'currency' | 'locale'> & { fallback?: string } = {},
): string {
  const parsed = parseCompactNumber(value, { integer: true });
  if (parsed === null || !Number.isFinite(parsed)) return options.fallback ?? '...';

  const unknownCurrencyValue = formatUnknownMinorCurrency(parsed, options);
  if (unknownCurrencyValue && !getSupportedCurrency(options.currency)) {
    return unknownCurrencyValue;
  }

  const normalizedCurrency = normalizeCurrency(options.currency);
  const currencyLocale = options.locale || CURRENCY_LOCALES[normalizedCurrency];
  const amount = parsed / 100;

  try {
    return amount.toLocaleString(currencyLocale, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `${getCurrencySymbol(normalizedCurrency)}${amount.toFixed(2)}`;
  }
}

export function formatSupportedMinorCurrency(
  value: NumericDisplayValue,
  options: FormatSupportedMinorCurrencyOptions = {},
): string {
  const parsed = parseCompactNumber(value, { integer: true });
  if (parsed === null || !Number.isFinite(parsed)) {
    return options.fallback ?? 'Price unavailable';
  }

  const supportedCurrency = getSupportedCurrency(options.currency);
  if (!supportedCurrency) {
    const unknownCurrencyValue = formatUnknownMinorCurrency(parsed, options);
    if (unknownCurrencyValue) {
      return unknownCurrencyValue;
    }
    return options.unavailableFallback ?? 'Amount captured · currency unavailable';
  }

  return formatMinorCurrency(parsed, {
    currency: supportedCurrency,
    locale: options.locale,
    fallback: options.fallback,
  });
}

export function formatSupportedCurrencyValue(
  amount: NumericDisplayValue,
  options: FormatSupportedCurrencyValueOptions = {},
): string {
  const parsed = parseCompactNumber(amount);
  if (parsed === null || !Number.isFinite(parsed)) {
    return options.fallback ?? 'Price unavailable';
  }

  const supportedCurrency = getSupportedCurrency(options.currency);
  if (!supportedCurrency) {
    const unknownCurrencyValue = formatUnknownCurrencyValue(parsed, options);
    if (unknownCurrencyValue) {
      return unknownCurrencyValue;
    }
    return options.unavailableFallback ?? 'Amount captured · currency unavailable';
  }

  const currencyLocale = options.locale || CURRENCY_LOCALES[supportedCurrency];

  return parsed.toLocaleString(currencyLocale, {
    style: 'currency',
    currency: supportedCurrency,
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });
}

export function coerceFiniteNumber(value: NumericDisplayValue, fallback = 0): number {
  const parsed = parseCompactNumber(value);
  return parsed ?? fallback;
}

function trimInsignificantDecimals(value: string): string {
  return value.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function getEffectiveWidth(options: Pick<FormatNumberOptions, 'screenWidth' | 'containerWidth'>): number | null {
  const screenWidth = getFinitePositiveNumber(options.screenWidth);
  const containerWidth = getFinitePositiveNumber(options.containerWidth);

  if (screenWidth !== null && containerWidth !== null) return Math.min(screenWidth, containerWidth);
  return containerWidth ?? screenWidth;
}

function getCharacterBudget(options: FormatNumberOptions): number | null {
  const explicitBudget = getFinitePositiveNumber(options.maxCharacters);
  if (explicitBudget !== null) return Math.max(1, Math.floor(explicitBudget));

  const width = getEffectiveWidth(options);
  if (width === null) return null;
  if (width <= 44) return 3;
  if (width <= 56) return 4;
  if (width <= 72) return 5;
  if (width <= 96) return 6;
  return null;
}

function resolveResponsiveNumberOptions(options: FormatNumberOptions): Required<Pick<FormatNumberOptions, 'minValue' | 'decimals' | 'showDecimalsForWhole'>> {
  const folding = options.folding ?? 'auto';
  const width = getEffectiveWidth(options);
  const characterBudget = getCharacterBudget(options);
  let minValue = options.minValue ?? DEFAULT_COMPACT_MIN_VALUE;
  let decimals = Math.max(0, options.decimals ?? 1);

  if (folding === 'full' && options.minValue === undefined) {
    minValue = Number.POSITIVE_INFINITY;
  } else if (folding === 'compact') {
    minValue = Math.min(minValue, DEFAULT_COMPACT_MIN_VALUE);
  } else if (width !== null && options.minValue === undefined) {
    if (width >= 240) minValue = 1_000_000;
    else if (width >= 160) minValue = 10_000;
    else minValue = DEFAULT_COMPACT_MIN_VALUE;
  }

  if (characterBudget !== null) {
    if (characterBudget <= 4) decimals = Math.min(decimals, 0);
    else if (characterBudget <= 6) decimals = Math.min(decimals, 1);
  }

  return {
    minValue,
    decimals,
    showDecimalsForWhole: options.showDecimalsForWhole ?? false,
  };
}

function formatCompactNumber(
  value: number,
  options: Required<Pick<FormatNumberOptions, 'minValue' | 'decimals' | 'showDecimalsForWhole'>>,
): string {
  const absNum = Math.abs(value);
  if (absNum < options.minValue) {
    return Number.isInteger(value) ? value.toString() : trimInsignificantDecimals(value.toString());
  }

  const sign = value < 0 ? '-' : '';
  let unitIndex = COMPACT_NUMBER_UNITS.findIndex((unit) => absNum >= unit.value);
  if (unitIndex === -1) unitIndex = COMPACT_NUMBER_UNITS.length - 1;

  const multiplier = 10 ** Math.max(0, options.decimals);
  let compactValue = Math.round((absNum / COMPACT_NUMBER_UNITS[unitIndex].value) * multiplier) / multiplier;

  if (compactValue >= 1000 && unitIndex > 0) {
    unitIndex -= 1;
    compactValue = Math.round((absNum / COMPACT_NUMBER_UNITS[unitIndex].value) * multiplier) / multiplier;
  }

  const formatted = compactValue.toFixed(Math.max(0, options.decimals));
  const finalValue = options.showDecimalsForWhole ? formatted : trimInsignificantDecimals(formatted);

  return `${sign}${finalValue}${COMPACT_NUMBER_UNITS[unitIndex].suffix}`;
}

function formatCompactNumberWithinBudget(
  value: number,
  options: Required<Pick<FormatNumberOptions, 'minValue' | 'decimals' | 'showDecimalsForWhole'>>,
  characterBudget: number | null,
): string {
  const formatted = formatCompactNumber(value, options);
  if (characterBudget === null || formatted.length <= characterBudget || options.decimals <= 0) {
    return formatted;
  }

  const tighter = formatCompactNumber(value, { ...options, decimals: 0, showDecimalsForWhole: false });
  return tighter.length <= formatted.length ? tighter : formatted;
}

export function formatNumber(
  num: NumericDisplayValue,
  options: FormatNumberOptions = {}
): string {
  const parsed = parseCompactNumber(num);
  if (parsed === null) return '...';

  const responsiveOptions = resolveResponsiveNumberOptions(options);
  const formatted = formatCompactNumberWithinBudget(parsed, responsiveOptions, getCharacterBudget(options));
  return formatted
    .replace(/K$/, options.thousandSuffix ?? 'K')
    .replace(/M$/, options.millionSuffix ?? 'M')
    .replace(/B$/, options.billionSuffix ?? 'B')
    .replace(/T$/, options.trillionSuffix ?? 'T');
}

export function formatSocialCount(
  value: NumericDisplayValue,
  options: FormatSocialCountOptions = {}
): string {
  const parsed = parseCompactNumber(value, { allowNegative: false, integer: true });
  const count = Math.max(0, parsed ?? 0);

  return formatNumber(count, options);
}

export function formatCurrency(
  amount: NumericDisplayValue,
  options: FormatCurrencyOptions = {}
): string {
  const parsed = parseCompactNumber(amount);
  if (parsed === null) return '...';

  const { currency = DEFAULT_CURRENCY, showSymbol = true, symbol, locale } = options;
  const { minValue } = resolveResponsiveNumberOptions(options);
  const unknownCurrencyValue = formatUnknownCurrencyValue(parsed, {
    currency,
    locale,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  if (unknownCurrencyValue && !getSupportedCurrency(currency)) {
    return unknownCurrencyValue;
  }
  const normalizedCurrency = normalizeCurrency(currency);
  const currencySymbol = symbol || (showSymbol ? CURRENCY_SYMBOLS[normalizedCurrency] : '');
  const currencyLocale = locale || CURRENCY_LOCALES[normalizedCurrency];

  if (Math.abs(parsed) < minValue) {
    return parsed.toLocaleString(currencyLocale, {
      style: 'currency', currency: normalizedCurrency, minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
  }

  const abbreviated = formatNumber(parsed, options);
  return showSymbol && currencySymbol ? `${currencySymbol}${abbreviated}` : abbreviated;
}

export function foldNumericText(value: string, options: FormatNumberOptions = {}): string {
  return value.replace(
    /(^|[^\w.])([+-]?(?:(?:\d{1,3}(?:,\d{3})+)|\d+)(?:\.\d+)?|\.\d+)(?:\s*(k|m|b|t|qa|qi|sx|sp|oc|no|thousand|million|billion|trillion|quadrillion|quintillion|sextillion|septillion|octillion|nonillion)s?\b)?/gi,
    (match, prefix: string) => {
      const parsed = parseCompactNumber(match.slice(prefix.length));
      return parsed === null ? match : `${prefix}${formatNumber(parsed, options)}`;
    },
  );
}

export function formatDate(date: number | Date | string, locale: string = 'en-GB'): string {
  const d = typeof date === 'string' ? new Date(date) : (typeof date === 'number' ? new Date(date) : date);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export interface PublicDisplayNameOptions {
  fallback?: string;
}

export interface FormatCourseNameOptions {
  courseId?: string | null;
  fallback?: string;
}

const COURSE_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  'compliance-course-v1': 'Compliance Course',
};

const COURSE_MINOR_WORDS = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);

function formatCourseNameWord(word: string, index: number): string {
  const lower = word.toLowerCase();

  if (lower === 'uk') return 'UK';
  if (/^v\d+$/.test(lower)) return lower.toUpperCase();
  if (index > 0 && COURSE_MINOR_WORDS.has(lower)) return lower;

  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}

export function formatCourseName(
  value: string | null | undefined,
  options: FormatCourseNameOptions = {},
): string {
  const fallback = options.fallback ?? 'Course';
  const source = (value ?? '').trim() || (options.courseId ?? '').trim();
  if (!source) return fallback;

  const override = COURSE_DISPLAY_NAME_OVERRIDES[source.toLowerCase()];
  if (override) return override;

  if (!/[-_]/.test(source)) return source;

  const displaySource = source.replace(/[-_]?v\d+$/i, '');
  const formatted = displaySource
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(formatCourseNameWord)
    .join(' ');

  return formatted || fallback;
}

export function stripInternalDemoWords(value: string): string {
  return value
    .replace(/\b(?:demo|test)\b/gi, ' ')
    .replace(/\s*[-_()\\[\\]{}:|]+\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function formatPublicDisplayName(
  value: string | null | undefined,
  options: PublicDisplayNameOptions = {}
): string {
  const fallback = options.fallback ?? 'User';
  if (typeof value !== 'string') return fallback;
  const cleaned = stripInternalDemoWords(value);
  return cleaned || fallback;
}

export function withPublicDisplayName<T extends { name?: string | null; fullName?: string | null }>(
  user: T | null | undefined,
  options: PublicDisplayNameOptions = {}
): T | null | undefined {
  if (user === null) return null;
  if (user === undefined) return undefined;
  const next = { ...user } as T;
  if (typeof next.name === 'string') {
    next.name = formatPublicDisplayName(next.name, options) as T['name'];
  }
  if (typeof next.fullName === 'string') {
    next.fullName = formatPublicDisplayName(next.fullName, options) as T['fullName'];
  }
  return next;
}
